/**
 * Investigations — virtualised events table + entity-detail drawer.
 *
 * The drawer is keyed by the `?entity=type:value` URL param so the
 * current view of a single IP / ASN / JA4 is a shareable link.
 */
import { useCallback, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useFilters } from "@/hooks/useFilters";
import { useAutoRefresh } from "@/hooks/useAutoRefresh";
import { useUrlValue } from "@/hooks/useUrlState";
import { Card, CardHeader, EmptyState, ErrorState, Skeleton, Button } from "@/components/primitives";
import { EventsTable } from "@/components/tables/EventsTable";
import { ReasonsKey } from "@/components/reasons/ReasonsKey";
import { CommonPivotStrip } from "@/components/investigations/CommonPivotStrip";
import {
  EntityDetailDrawer,
  encodeEntityParam,
  parseEntityParam,
} from "@/components/drawer/EntityDetailDrawer";
import type { EntityTypeV1 } from "@/types/v1";
import type { FilterState } from "@/types";
import { fmtNumber } from "@/lib/format";
import { endpointAreaGroups } from "@/config/filters";

const PAGE_SIZE = 50;

/**
 * Map an entity type clicked in the events table to the global filter
 * key it should narrow. We narrow the table on click so the rows behind
 * the drawer match the entity the user just opened — anything else is
 * confusing ("I clicked one IP, why am I still seeing all of them?").
 */
const ENTITY_TO_FILTER: Partial<Record<EntityTypeV1, keyof FilterState>> = {
  ip: "ip",
  asn: "asn",
  ja4: "ja4",
  ja3: "ja3",
  email: "email",
  business: "businessId",
  session: "sessionId",
  country: "country",
  managedList: "managedList",
  detectionId: "detectionId",
  endpointGroup: "endpointGroups",
};

export function Investigations() {
  const { filters, patchFilters } = useFilters();
  const { tick } = useAutoRefresh();
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState<{ id: string; desc: boolean }>({ id: "ts", desc: true });
  const [entityParam, setEntityParam] = useUrlValue("entity");

  const events = useQuery({
    queryKey: ["events", filters, page, sort, tick],
    queryFn: ({ signal }) => api.events(filters, {
      page, pageSize: PAGE_SIZE, sort: sort.id, sortDir: sort.desc ? "desc" : "asc",
    }, signal),
    placeholderData: (prev) => prev,
  });

  const data = events.data?.data;
  const totalPages = data ? Math.max(1, Math.ceil(data.total / PAGE_SIZE)) : 1;

  const openEntity = useCallback((type: EntityTypeV1, value: string) => {
    // Narrow the underlying table to the same entity, then open the
    // drawer with the deep-dive. endpointGroups is an array filter, so
    // pass a single-element list.
    const key = ENTITY_TO_FILTER[type];
    if (key) {
      const patch = (key === "endpointGroups"
        ? { endpointGroups: endpointAreaGroups(value) }
        : { [key]: value }) as Partial<FilterState>;
      patchFilters(patch);
      setPage(1);
    }
    setEntityParam(encodeEntityParam(type, value));
  }, [patchFilters, setEntityParam]);

  const closeEntity = useCallback(() => setEntityParam(undefined), [setEntityParam]);
  const entity = useMemo(() => parseEntityParam(entityParam), [entityParam]);

  return (
    <div className="flex flex-col gap-4">
      <ReasonsKey />
      <CommonPivotStrip />
      <Card>
        <CardHeader
          title="Risk events"
          subtitle={data ? `${fmtNumber(data.total, { compact: true })} events match the active filters` : "Loading…"}
          action={
            <div className="flex items-center gap-2 text-xs text-fg-muted">
              <span>Page {page} / {totalPages}</span>
              <Button size="sm" variant="ghost" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>Prev</Button>
              <Button size="sm" variant="ghost" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>Next</Button>
            </div>
          }
        />
        {events.isLoading && !data ? (
          <Skeleton className="h-[60vh]" />
        ) : events.error ? (
          <ErrorState error={events.error} onRetry={() => events.refetch()} />
        ) : !data?.rows.length ? (
          <EmptyState title="No events match the current filters." hint="Try widening the time range or removing a filter." />
        ) : (
          <EventsTable
            rows={data.rows}
            sort={sort}
            onSortChange={(s) => { setSort(s); setPage(1); }}
            onEntity={openEntity}
          />
        )}
      </Card>

      <EntityDetailDrawer entity={entity} onClose={closeEntity} onPivot={openEntity} />
    </div>
  );
}
